<template>
  <svg
    v-if="isPowerOverlayActive && !sketchTrimPowerShiftPressed"
    class="trim-power-overlay"
  >
    <polyline
      v-if="sketchTrimPowerTrailPolyline"
      :points="sketchTrimPowerTrailPolyline"
      fill="none"
      stroke="#ef4444"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-dasharray="4 2"
      opacity="0.9"
    />
    <rect
      v-for="(marker, index) in sketchTrimPowerMarkers"
      :key="`trim-marker-${index}`"
      :x="marker.x - 4"
      :y="marker.y - 4"
      width="8"
      height="8"
      fill="#ef4444"
      stroke="#ffffff"
      stroke-width="1"
      rx="1"
      ry="1"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import * as THREE from 'three';
import type { Subscription } from 'rxjs';
import { RawPointerEventType } from '@puppycad/cad-core';
import type { Viewer } from '@puppycad/cad-core';
import { SketchTrimMode } from '@/core';
import type { SketchTrimMode as SketchTrimModeType } from '@/core';
import { useViewer } from '@/hooks';

/**
 * 强力剪裁覆盖层属性。
 */
interface SketchTrimPowerOverlayProps {
  /**
   * 视口容器元素。
   */
  viewportContainer?: HTMLElement | null;
  /**
   * 当前剪裁模式。
   */
  sketchTrimMode?: SketchTrimModeType | null;
  /**
   * 当前是否处于剪裁工作流中。
   */
  isWorkflowActive?: boolean;
}

const props = withDefaults(defineProps<SketchTrimPowerOverlayProps>(), {
  viewportContainer: null,
  sketchTrimMode: null,
  isWorkflowActive: false,
});

const { getViewer } = useViewer();

/**
 * 强力剪裁覆盖层点位。
 */
interface SketchTrimPowerOverlayPoint {
  /** 屏幕 X 坐标。 */
  x: number;
  /** 屏幕 Y 坐标。 */
  y: number;
}

/**
 * 强力剪裁指针阶段枚举。
 */
enum SketchTrimPowerPointerStage {
  /** 空闲态。 */
  Idle = 'idle',
  /** 拖拽记录态。 */
  Dragging = 'dragging',
}

/** 强力剪裁轨迹采样阈值。 */
const SKETCH_TRIM_POWER_TRAIL_SAMPLE_STEP_PX = 2;
/** 强力剪裁标记触发还原半径。 */
const SKETCH_TRIM_POWER_UNDO_TRIGGER_RADIUS_PX = 10;
/** 强力剪裁标记重置触发器半径。 */
const SKETCH_TRIM_POWER_UNDO_REARM_RADIUS_PX = 14;

/** 强力剪裁事件订阅集合。 */
const overlaySubscriptions = ref<Subscription[]>([]);
/** 强力剪裁指针阶段。 */
const sketchTrimPowerPointerStage = ref<SketchTrimPowerPointerStage>(SketchTrimPowerPointerStage.Idle);
/** 强力剪裁路径点集合。 */
const sketchTrimPowerTrailPoints = ref<SketchTrimPowerOverlayPoint[]>([]);
/** 强力剪裁标记点集合。 */
const sketchTrimPowerMarkers = ref<SketchTrimPowerOverlayPoint[]>([]);
/** 最近一次同步的撤销版本号。 */
const sketchTrimPowerLastUndoToken = ref<number | null>(null);
/** 撤销触发重置标记。 */
const sketchTrimPowerUndoRequireRearm = ref(false);
/** 当前 SDK/鼠标事件中的 Shift 状态，隐藏延伸期间的整层剪裁提示。 */
const sketchTrimPowerShiftPressed = ref(false);

/**
 * 获取当前可用的 Viewer。
 *
 * @returns 当前共享 Viewer。
 */
function resolveViewer(): Viewer | null {
  return getViewer();
}

/**
 * 判断强力剪裁覆盖层是否应当激活。
 */
const isPowerOverlayActive = computed<boolean>(() => {
  return props.isWorkflowActive && props.sketchTrimMode === SketchTrimMode.Power;
});

/**
 * 强力剪裁路径折线字符串。
 */
const sketchTrimPowerTrailPolyline = computed<string>(() => {
  if (sketchTrimPowerTrailPoints.value.length <= 1) {
    return '';
  }
  return sketchTrimPowerTrailPoints.value
    .map((point) => `${point.x},${point.y}`)
    .join(' ');
});

/**
 * 清理强力剪裁覆盖层状态。
 */
function clearSketchTrimPowerOverlayState(): void {
  sketchTrimPowerPointerStage.value = SketchTrimPowerPointerStage.Idle;
  sketchTrimPowerTrailPoints.value = [];
  sketchTrimPowerMarkers.value = [];
  sketchTrimPowerLastUndoToken.value = null;
  sketchTrimPowerUndoRequireRearm.value = false;
}

/**
 * 将客户端坐标映射为覆盖层坐标。
 *
 * @param clientX 客户端 X 坐标。
 * @param clientY 客户端 Y 坐标。
 * @returns 覆盖层坐标点。
 */
function mapClientPointToTrimOverlayPoint(clientX: number, clientY: number): SketchTrimPowerOverlayPoint {
  const container = props.viewportContainer;
  if (!container) {
    throw new Error('强力剪裁覆盖层映射失败：视口容器未就绪。');
  }
  const rect = container.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
}

/**
 * 将世界坐标映射为覆盖层坐标。
 *
 * @param worldPoint 世界坐标。
 * @returns 覆盖层坐标点。
 */
function mapWorldPointToTrimOverlayPoint(worldPoint: [number, number, number]): SketchTrimPowerOverlayPoint {
  const viewer = resolveViewer();
  if (!viewer || !viewer.camera) {
    throw new Error('强力剪裁覆盖层映射失败：相机未就绪。');
  }
  const container = props.viewportContainer;
  if (!container) {
    throw new Error('强力剪裁覆盖层映射失败：视口容器未就绪。');
  }
  const rect = container.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    throw new Error('强力剪裁覆盖层映射失败：视口尺寸非法。');
  }

  const projected = new THREE.Vector3(worldPoint[0], worldPoint[1], worldPoint[2]).project(viewer.camera);
  return {
    x: ((projected.x + 1) * 0.5) * rect.width,
    y: ((1 - projected.y) * 0.5) * rect.height,
  };
}

/**
 * 开始记录强力剪裁鼠标轨迹。
 *
 * @param clientX 客户端 X 坐标。
 * @param clientY 客户端 Y 坐标。
 */
function startSketchTrimPowerTrail(clientX: number, clientY: number): void {
  sketchTrimPowerPointerStage.value = SketchTrimPowerPointerStage.Dragging;
  sketchTrimPowerMarkers.value = [];
  sketchTrimPowerLastUndoToken.value = null;
  sketchTrimPowerUndoRequireRearm.value = false;
  sketchTrimPowerTrailPoints.value = [mapClientPointToTrimOverlayPoint(clientX, clientY)];
}

/**
 * 追加强力剪裁鼠标轨迹采样点。
 *
 * @param clientX 客户端 X 坐标。
 * @param clientY 客户端 Y 坐标。
 */
function appendSketchTrimPowerTrail(clientX: number, clientY: number): void {
  if (sketchTrimPowerPointerStage.value !== SketchTrimPowerPointerStage.Dragging) {
    return;
  }
  const nextPoint = mapClientPointToTrimOverlayPoint(clientX, clientY);
  const lastPoint = sketchTrimPowerTrailPoints.value[sketchTrimPowerTrailPoints.value.length - 1];
  if (!lastPoint) {
    sketchTrimPowerTrailPoints.value = [nextPoint];
    return;
  }

  const distance = Math.hypot(nextPoint.x - lastPoint.x, nextPoint.y - lastPoint.y);
  if (distance < SKETCH_TRIM_POWER_TRAIL_SAMPLE_STEP_PX) {
    return;
  }

  // 将满足采样间距的新点追加到折线尾部。
  sketchTrimPowerTrailPoints.value = [...sketchTrimPowerTrailPoints.value, nextPoint];
}

/**
 * 结束强力剪裁轨迹记录。
 */
function finishSketchTrimPowerTrail(): void {
  sketchTrimPowerPointerStage.value = SketchTrimPowerPointerStage.Idle;
  sketchTrimPowerTrailPoints.value = [];
  sketchTrimPowerMarkers.value = [];
  sketchTrimPowerLastUndoToken.value = null;
  sketchTrimPowerUndoRequireRearm.value = false;
}

/**
 * 同步强力剪裁最近一次裁剪标记点。
 */
function syncSketchTrimPowerMarkerFromController(): void {
  if (!isPowerOverlayActive.value) {
    clearSketchTrimPowerOverlayState();
    return;
  }
  if (sketchTrimPowerShiftPressed.value) {
    // 微任务执行时重新读取当前状态，禁止先前排队的同步把延伸期间的方块重新画出。
    sketchTrimPowerMarkers.value = [];
    sketchTrimPowerLastUndoToken.value = null;
    sketchTrimPowerUndoRequireRearm.value = true;
    return;
  }
  if (sketchTrimPowerPointerStage.value !== SketchTrimPowerPointerStage.Dragging) {
    sketchTrimPowerMarkers.value = [];
    sketchTrimPowerLastUndoToken.value = null;
    sketchTrimPowerUndoRequireRearm.value = false;
    return;
  }

  const markerSnapshot = resolveViewer()?.getLastPowerTrimMarkerSnapshot?.() as {
    undoToken: number;
    worldPoint: [number, number, number];
  } | null;
  if (!markerSnapshot) {
    sketchTrimPowerMarkers.value = [];
    sketchTrimPowerLastUndoToken.value = null;
    return;
  }
  if (markerSnapshot.undoToken === sketchTrimPowerLastUndoToken.value) {
    return;
  }

  //------ 强力剪裁撤销重置门槛 开始------
  // 新标记刚生成时，鼠标往往仍停留在裁剪命中点附近。
  // 若立即允许撤销判定，同一段连续拖拽的下一帧 MouseMove 就会命中红方块，
  // 造成“刚裁掉又立刻还原”的误触行为。
  // 因此必须先要求指针离开 rearm 半径，再允许重新进入触发半径执行撤销。
  sketchTrimPowerUndoRequireRearm.value = true;
  //------ 强力剪裁撤销重置门槛 结束------
  sketchTrimPowerMarkers.value = [mapWorldPointToTrimOverlayPoint(markerSnapshot.worldPoint)];
  sketchTrimPowerLastUndoToken.value = markerSnapshot.undoToken;
}

/**
 * 异步排队同步强力剪裁标记点。
 */
function queueSyncSketchTrimPowerMarkerFromController(): void {
  queueMicrotask(() => {
    if (!resolveViewer()) {
      return;
    }
    syncSketchTrimPowerMarkerFromController();
  });
}

/**
 * 同步 Shift 显示状态，覆盖静止指针按键和鼠标事件两种输入来源。
 * @param pressed 当前是否按住 Shift。
 */
function syncSketchTrimPowerShiftState(pressed: boolean): void {
  if (sketchTrimPowerShiftPressed.value === pressed) {
    return;
  }
  sketchTrimPowerShiftPressed.value = pressed;
  sketchTrimPowerTrailPoints.value = [];
  sketchTrimPowerMarkers.value = [];
  sketchTrimPowerLastUndoToken.value = null;
  // 释放 Shift 后恢复旧裁剪方块也必须重新移出，避免在原位置直接触发回退。
  sketchTrimPowerUndoRequireRearm.value = true;
  queueSyncSketchTrimPowerMarkerFromController();
}

/**
 * 鼠标移入最近一次强力剪裁点时触发还原。
 *
 * @param clientX 客户端 X 坐标。
 * @param clientY 客户端 Y 坐标。
 */
function handleSketchTrimPowerUndoHover(clientX: number, clientY: number): void {
  if (sketchTrimPowerShiftPressed.value) {
    return;
  }
  if (!isPowerOverlayActive.value) {
    sketchTrimPowerUndoRequireRearm.value = false;
    return;
  }
  if (sketchTrimPowerPointerStage.value !== SketchTrimPowerPointerStage.Dragging) {
    return;
  }
  const marker = sketchTrimPowerMarkers.value[0];
  if (!marker) {
    return;
  }

  const cursorPoint = mapClientPointToTrimOverlayPoint(clientX, clientY);
  const distance = Math.hypot(cursorPoint.x - marker.x, cursorPoint.y - marker.y);

  if (sketchTrimPowerUndoRequireRearm.value) {
    if (distance >= SKETCH_TRIM_POWER_UNDO_REARM_RADIUS_PX) {
      sketchTrimPowerUndoRequireRearm.value = false;
    }
    return;
  }

  if (distance > SKETCH_TRIM_POWER_UNDO_TRIGGER_RADIUS_PX) {
    return;
  }

  const undoSucceeded = resolveViewer()?.undoLastPowerTrim?.() ?? false;
  if (!undoSucceeded) {
    throw new Error('强力剪裁回退失败：缺少可回退记录。');
  }

  sketchTrimPowerUndoRequireRearm.value = true;
  syncSketchTrimPowerMarkerFromController();
}

/**
 * 清理强力剪裁事件订阅。
 */
function cleanupOverlaySubscriptions(): void {
  overlaySubscriptions.value.forEach((subscription) => subscription.unsubscribe());
  overlaySubscriptions.value = [];
}

/**
 * 建立强力剪裁覆盖层所需的 Viewer 事件订阅。
 */
function subscribeOverlayEvents(): void {
  cleanupOverlaySubscriptions();
  const viewer = resolveViewer();
  if (!viewer?.eventManager) {
    return;
  }
  syncSketchTrimPowerShiftState(Boolean(viewer.eventManager.isShiftPressed));
  const modifierKeysSubscription = viewer.eventManager.observeModifierKeysChanged()?.subscribe(() => {
    syncSketchTrimPowerShiftState(viewer.eventManager.isShiftPressed);
  });

  const trimPowerMouseDownSubscription = viewer.eventManager.observe(
    RawPointerEventType.MouseDown,
  )?.subscribe((payload) => {
    if (!isPowerOverlayActive.value) {
      return;
    }
    if (payload.button !== 0) {
      return;
    }
    syncSketchTrimPowerShiftState(Boolean(payload.shiftKey));
    startSketchTrimPowerTrail(payload.clientX, payload.clientY);
    queueSyncSketchTrimPowerMarkerFromController();
  });

  const trimPowerMouseMoveSubscription = viewer.eventManager.observe(
    RawPointerEventType.MouseMove,
  )?.subscribe((payload) => {
    if (!isPowerOverlayActive.value) {
      return;
    }
    syncSketchTrimPowerShiftState(Boolean(payload.shiftKey));
    if (payload.shiftKey) {
      // 延伸跟随锁定实体，不显示横扫剪裁轨迹，也不允许经过红方块时误撤销。
      sketchTrimPowerTrailPoints.value = [];
      sketchTrimPowerUndoRequireRearm.value = true;
    } else {
      appendSketchTrimPowerTrail(payload.clientX, payload.clientY);
      handleSketchTrimPowerUndoHover(payload.clientX, payload.clientY);
    }
    queueSyncSketchTrimPowerMarkerFromController();
  });

  const trimPowerMouseUpSubscription = viewer.eventManager.observe(
    RawPointerEventType.MouseUp,
  )?.subscribe((payload) => {
    if (!isPowerOverlayActive.value) {
      return;
    }
    if (payload.button !== 0) {
      return;
    }
    syncSketchTrimPowerShiftState(Boolean(payload.shiftKey));
    finishSketchTrimPowerTrail();
    queueSyncSketchTrimPowerMarkerFromController();
  });

  const trimPowerClickSubscription = viewer.eventManager.observe(
    RawPointerEventType.Click,
  )?.subscribe(() => {
    if (!props.isWorkflowActive) {
      clearSketchTrimPowerOverlayState();
      return;
    }
    queueSyncSketchTrimPowerMarkerFromController();
  });

  const trimPowerCancelSubscription = viewer.eventManager.observe(
    RawPointerEventType.PointerCancel,
  )?.subscribe(() => {
    // SDK 已取消模型事务，覆盖层只清理临时显示。
    clearSketchTrimPowerOverlayState();
  });

  overlaySubscriptions.value = [
    trimPowerMouseDownSubscription,
    trimPowerMouseMoveSubscription,
    trimPowerMouseUpSubscription,
    trimPowerClickSubscription,
    trimPowerCancelSubscription,
    modifierKeysSubscription,
  ].filter((subscription): subscription is Subscription => subscription !== undefined);
}

watch(
  () => [props.isWorkflowActive, props.sketchTrimMode, props.viewportContainer],
  () => {
    subscribeOverlayEvents();
  },
  {
    immediate: true,
  },
);

watch(
  () => [props.isWorkflowActive, props.sketchTrimMode, props.viewportContainer],
  () => {
    if (!isPowerOverlayActive.value) {
      clearSketchTrimPowerOverlayState();
      return;
    }
    syncSketchTrimPowerMarkerFromController();
  },
  {
    immediate: true,
  },
);

onBeforeUnmount(() => {
  clearSketchTrimPowerOverlayState();
  cleanupOverlaySubscriptions();
});
</script>

<style scoped>
.trim-power-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 8;
}
</style>
